import React, {useState, useEffect} from "react";
import Alert from "react-s-alert";
import {AdmissionsService} from "../../services/AdmissionsService";
import {StarIcon} from "./style";
import {theme} from "@reside/ui";

// Hook for managing star state
export const useStarAdmission = (
  admissionId: string,
  initialStarred = false,
) => {
  const [isStarred, setIsStarred] = useState(initialStarred);
  const [isLoading, setIsLoading] = useState(false);

  // Sync internal state when admission or starred status changes
  useEffect(() => {
    setIsStarred(initialStarred);
  }, [admissionId, initialStarred]);

  const toggleStar = async () => {
    // Allow multiple clicks but prevent multiple simultaneous requests
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    // Optimistic update - immediately update UI and count
    const previousState = isStarred;
    setIsStarred(!isStarred);

    // Optimistically update count for immediate UI feedback
    const currentCount = parseInt(
      sessionStorage.getItem("totalStarredAdmissions") || "0",
      10,
    );
    const newCount = previousState ? currentCount - 1 : currentCount + 1;

    // Avoid optimistic 0 to keep filter enabled
    const shouldOptimisticallyUpdateCount = !(
      previousState && currentCount === 1
    );

    if (shouldOptimisticallyUpdateCount) {
      sessionStorage.setItem("totalStarredAdmissions", newCount.toString());
      window.dispatchEvent(new Event("starredAdmissionsUpdated"));
    }

    try {
      if (previousState) {
        await AdmissionsService.unstarAdmission(admissionId);
      } else {
        await AdmissionsService.starAdmission(admissionId);
      }

      // Apply deferred count update on success
      if (!shouldOptimisticallyUpdateCount) {
        sessionStorage.setItem("totalStarredAdmissions", newCount.toString());
        window.dispatchEvent(new Event("starredAdmissionsUpdated"));
      }

      // Only trigger filter refetch after API success
      window.dispatchEvent(new Event("admissionStarToggled"));
    } catch (error) {
      // Revert optimistic updates on error
      setIsStarred(previousState);
      if (shouldOptimisticallyUpdateCount) {
        sessionStorage.setItem(
          "totalStarredAdmissions",
          currentCount.toString(),
        );
        window.dispatchEvent(new Event("starredAdmissionsUpdated"));
      }

      // eslint-disable-next-line no-console
      console.error({starError: error});
      Alert.error("Failed to star admission");
    } finally {
      setIsLoading(false);
    }
  };

  return {isStarred, isLoading, toggleStar};
};

// Interactive star icon component
type StarAdmissionIconProps = {
  admissionId: string;
  isStarred?: boolean;
};

export const StarAdmissionIcon = ({
  admissionId,
  isStarred = false,
}: StarAdmissionIconProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const {
    isStarred: starred,
    isLoading,
    toggleStar,
  } = useStarAdmission(admissionId, isStarred);

  const iconName = starred ? "star" : "star-empty";

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Allow clicks even when loading - the hook will handle request protection
    toggleStar();
  };

  const handleMouseEnter = () => {
    // Always allow hover effects
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <StarIcon
      color={
        starred
          ? theme.colors.golden90
          : isHovered
          ? theme.colors.darkBlue80
          : theme.colors.darkBlue60
      }
      name={iconName}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      isLoading={isLoading}
      isHovered={isHovered}
    />
  );
};
